import { useEffect } from 'react';

interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  noIndex?: boolean;
}

const BASE_TITLE = 'Packet Prospector';
const DEFAULT_DESCRIPTION = 'Streamline your ISP operations with Packet Prospector. Manage leads, map service areas, track installations, and grow your fiber internet business.';
const BASE_URL = 'https://packetprospector.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

/**
 * Custom hook for managing page-level SEO meta tags
 * Updates document title and meta tags when component mounts
 * Restores defaults when component unmounts
 */
export function useSEO(config: SEOConfig) {
  useEffect(() => {
    // Store original values to restore on unmount
    const originalTitle = document.title;
    const originalMetas: { name: string; content: string }[] = [];

    // Helper to update or create meta tag
    const setMetaTag = (nameOrProperty: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${nameOrProperty}"]`) as HTMLMetaElement | null;

      if (meta) {
        originalMetas.push({ name: nameOrProperty, content: meta.content });
        meta.content = content;
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, nameOrProperty);
        meta.content = content;
        document.head.appendChild(meta);
      }
    };

    // Set document title
    if (config.title) {
      document.title = `${config.title} | ${BASE_TITLE}`;
    }

    // Set description
    if (config.description) {
      setMetaTag('description', config.description);
    }

    // Set keywords
    if (config.keywords) {
      setMetaTag('keywords', config.keywords);
    }

    // Set robots
    if (config.noIndex) {
      setMetaTag('robots', 'noindex, nofollow');
    }

    // Set canonical URL
    if (config.canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = config.canonicalUrl;
    }

    // Open Graph tags
    setMetaTag('og:title', config.ogTitle || config.title || BASE_TITLE, true);
    setMetaTag('og:description', config.ogDescription || config.description || DEFAULT_DESCRIPTION, true);
    if (config.ogImage) {
      setMetaTag('og:image', config.ogImage, true);
    }
    if (config.ogType) {
      setMetaTag('og:type', config.ogType, true);
    }
    if (config.canonicalUrl) {
      setMetaTag('og:url', config.canonicalUrl, true);
    }

    // Twitter tags
    if (config.twitterCard) {
      setMetaTag('twitter:card', config.twitterCard);
    }
    setMetaTag('twitter:title', config.ogTitle || config.title || BASE_TITLE);
    setMetaTag('twitter:description', config.ogDescription || config.description || DEFAULT_DESCRIPTION);
    if (config.ogImage) {
      setMetaTag('twitter:image', config.ogImage);
    }

    // Cleanup function - restore original values
    return () => {
      document.title = originalTitle;
      // Note: We don't remove added meta tags to avoid flickering
      // The next page will overwrite them with its own values
    };
  }, [
    config.title,
    config.description,
    config.keywords,
    config.canonicalUrl,
    config.ogTitle,
    config.ogDescription,
    config.ogImage,
    config.ogType,
    config.twitterCard,
    config.noIndex,
  ]);
}

// Pre-configured SEO configs for common pages
export const SEO_CONFIGS = {
  home: {
    title: 'Complete ISP Operations Platform',
    description: 'All-in-one platform for ISPs. Manage leads, service areas, installations, technicians, billing, customer portal, fiber mapping, and grow your internet service provider business.',
    keywords: 'ISP software, fiber internet, lead management, service area mapping, installation scheduling, billing software, customer portal, internet service provider, broadband management, fiber mapping',
    canonicalUrl: BASE_URL,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    twitterCard: 'summary_large_image' as const,
  },
  pricing: {
    title: 'ISP Software Pricing Plans',
    description: 'Flexible pricing plans for ISPs of all sizes. Start with a 14-day free trial and scale as you grow. No charges until trial ends.',
    keywords: 'ISP software pricing, fiber internet software cost, broadband management pricing, ISP subscription plans',
    canonicalUrl: `${BASE_URL}/pricing`,
    ogImage: DEFAULT_OG_IMAGE,
    twitterCard: 'summary_large_image' as const,
  },
  toolsIndex: {
    title: 'Free Tools for ISPs: FCC Labels, PON Link Budgets, Fiber Color Codes',
    description: 'Free tools for internet service providers: an FCC broadband label generator, a GPON and XGS-PON link budget calculator, and a fiber color code lookup. No account, no email.',
    keywords: 'free ISP tools, WISP tools, fiber tools, FCC broadband label generator, PON link budget calculator, fiber color code',
    canonicalUrl: `${BASE_URL}/tools`,
    ogImage: DEFAULT_OG_IMAGE,
    twitterCard: 'summary_large_image' as const,
  },
  broadbandLabelTool: {
    title: 'Free FCC Broadband Label Generator for ISPs',
    description: 'Create an FCC-compliant Broadband Facts consumer label and the required machine-readable CSV for your internet plans. Free, no account, with a built-in check for missing rows.',
    keywords: 'FCC broadband label generator, broadband facts label, broadband nutrition label, broadband consumer label CSV, machine-readable broadband label, ISP FCC compliance',
    canonicalUrl: `${BASE_URL}/tools/broadband-label`,
    ogImage: DEFAULT_OG_IMAGE,
    twitterCard: 'summary_large_image' as const,
  },
  ponCalculator: {
    title: 'Free PON Link Budget Calculator (GPON and XGS-PON)',
    description: 'Check a GPON or XGS-PON optical link budget: splitters, splices, connectors and fiber loss against ITU-T optic classes, with margin, maximum reach and a shareable result. Free, no account.',
    keywords: 'PON link budget calculator, GPON power budget, XGS-PON link budget, fiber loss calculator, optical splitter loss, PON calculator, FTTH loss budget',
    canonicalUrl: `${BASE_URL}/tools/pon-calculator`,
    ogImage: DEFAULT_OG_IMAGE,
    twitterCard: 'summary_large_image' as const,
  },
  fiberColorCode: {
    title: 'Fiber Color Code Lookup (TIA-598): Find Any Fiber by Number or Color',
    description: 'Which color is fiber 37 in a 144? Find any fiber by number or by color in a TIA-598 color-coded cable, 6 to 432 fibers, and print a chart of every tube. Free, no account.',
    keywords: 'fiber color code, TIA-598 color code, fiber optic color code chart, buffer tube color code, 144 fiber color code, fiber strand color lookup',
    canonicalUrl: `${BASE_URL}/tools/fiber-color-code`,
    ogImage: DEFAULT_OG_IMAGE,
    twitterCard: 'summary_large_image' as const,
  },
  login: {
    title: 'Sign In to Your ISP Dashboard',
    description: 'Sign in to your Packet Prospector account to manage leads, installations, billing, service areas, and grow your ISP business.',
    canonicalUrl: `${BASE_URL}/login`,
    noIndex: true, // Don't index login page
  },
  register: {
    title: 'Start Your Free Trial',
    description: 'Start your 14-day free trial of Packet Prospector. Complete ISP operations platform with lead management, billing, customer portal, and more. No charges until trial ends.',
    canonicalUrl: `${BASE_URL}/register`,
    ogImage: DEFAULT_OG_IMAGE,
    twitterCard: 'summary_large_image' as const,
  },
};

export default useSEO;
